/**
 * Merchant data generator
 * Generates 75 merchants with realistic A55 field patterns
 */

import { faker } from '@faker-js/faker'
import type {
  Merchant,
  MerchantCategory,
  MerchantConfig,
  MerchantDailyData,
  MerchantListItem,
  MerchantStatistics,
  MerchantStatus,
  MerchantVolumeByCurrency,
  MerchantVolumeByFlag,
  MerchantWithMetrics
} from '../../shared/types/merchants'
import type { Address, ContactInfo, CountryCode, CurrencyCode } from '../../shared/types/common'

// Configuration
export const MERCHANT_CONFIG = {
  TOTAL_COUNT: 75,
  SEED: 12345,

  STATUS_WEIGHTS: {
    active: 0.70,
    inactive: 0.12,
    pending: 0.10,
    suspended: 0.05,
    blocked: 0.03
  },

  CATEGORIES: [
    'e_commerce',
    'retail',
    'services',
    'digital_goods',
    'travel',
    'food_delivery',
    'gaming',
    'subscription',
    'marketplace',
    'other'
  ] as MerchantCategory[],

  COUNTRIES: [
    { code: 'BR' as CountryCode, weight: 0.45, timezone: 'America/Sao_Paulo' },
    { code: 'MX' as CountryCode, weight: 0.20, timezone: 'America/Mexico_City' },
    { code: 'CL' as CountryCode, weight: 0.12, timezone: 'America/Santiago' },
    { code: 'CO' as CountryCode, weight: 0.10, timezone: 'America/Bogota' },
    { code: 'PE' as CountryCode, weight: 0.08, timezone: 'America/Lima' },
    { code: 'AR' as CountryCode, weight: 0.05, timezone: 'America/Argentina/Buenos_Aires' }
  ],

  LEGAL_TYPES: ['individual', 'company', 'partnership'] as const,

  INDUSTRIES: [
    'Technology', 'Retail', 'Financial Services', 'E-commerce',
    'Healthcare', 'Education', 'Entertainment', 'Travel',
    'Food & Beverage', 'Real Estate', 'Logistics', 'Manufacturing'
  ]
}

/**
 * Weighted random selection helper
 */
function weightedRandom<T>(items: Array<{ value: T, weight: number }>): T {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0)
  const random = Math.random() * totalWeight

  let currentWeight = 0
  for (const item of items) {
    currentWeight += item.weight
    if (random <= currentWeight) {
      return item.value
    }
  }

  return items[items.length - 1]!.value
}

/**
 * Generate merchant status with weighted distribution
 */
function generateStatus(): MerchantStatus {
  return weightedRandom([
    { value: 'active' as MerchantStatus, weight: MERCHANT_CONFIG.STATUS_WEIGHTS.active },
    { value: 'inactive' as MerchantStatus, weight: MERCHANT_CONFIG.STATUS_WEIGHTS.inactive },
    { value: 'pending' as MerchantStatus, weight: MERCHANT_CONFIG.STATUS_WEIGHTS.pending },
    { value: 'suspended' as MerchantStatus, weight: MERCHANT_CONFIG.STATUS_WEIGHTS.suspended },
    { value: 'blocked' as MerchantStatus, weight: MERCHANT_CONFIG.STATUS_WEIGHTS.blocked }
  ])
}

/**
 * Generate country with weighted distribution
 */
function generateCountry(): { code: CountryCode, timezone: string } {
  const country = weightedRandom(
    MERCHANT_CONFIG.COUNTRIES.map(c => ({ value: c, weight: c.weight }))
  )
  return { code: country.code, timezone: country.timezone }
}

/**
 * Generate realistic address based on country
 */
function generateAddress(countryCode: CountryCode): Address {
  const stateMap: Record<CountryCode, string[]> = {
    BR: ['SP', 'RJ', 'MG', 'RS', 'PR', 'SC', 'BA', 'PE'],
    MX: ['CDMX', 'JAL', 'NL', 'GTO', 'PUE', 'QRO', 'YUC', 'BCN'],
    CL: ['RM', 'VAL', 'BIO', 'MAU', 'ARI', 'ANT', 'COQ', 'LLA'],
    CO: ['CUN', 'ANT', 'VAL', 'ATL', 'SAN', 'BOL', 'NAR', 'TOL'],
    PE: ['LIM', 'ARE', 'LAL', 'CAL', 'PIU', 'CUS', 'JUN', 'LAM'],
    AR: ['CABA', 'BA', 'CBA', 'SFE', 'MZA', 'TUC', 'SAL', 'ENR'],
    US: ['CA', 'NY', 'TX', 'FL', 'IL', 'PA', 'OH', 'GA']
  }

  return {
    street: faker.location.streetAddress(),
    number: faker.number.int({ min: 1, max: 9999 }).toString(),
    complement: faker.helpers.maybe(() => faker.location.secondaryAddress(), { probability: 0.3 }),
    neighborhood: faker.location.county(),
    city: faker.location.city(),
    state: faker.helpers.arrayElement(stateMap[countryCode] || ['Unknown']),
    country: getCountryName(countryCode),
    countryCode,
    postalCode: generatePostalCode(countryCode)
  }
}

/**
 * Get country name from code
 */
function getCountryName(code: CountryCode): string {
  const names: Record<CountryCode, string> = {
    BR: 'Brazil',
    MX: 'Mexico',
    CL: 'Chile',
    CO: 'Colombia',
    PE: 'Peru',
    AR: 'Argentina',
    US: 'United States'
  }
  return names[code] || code
}

/**
 * Generate postal code based on country format
 */
function generatePostalCode(countryCode: CountryCode): string {
  switch (countryCode) {
    case 'BR':
      return `${faker.string.numeric(5)}-${faker.string.numeric(3)}`
    case 'MX':
      return faker.string.numeric(5)
    case 'CL':
      return faker.string.numeric(7)
    case 'CO':
      return faker.string.numeric(6)
    case 'PE':
      return faker.string.numeric(5)
    case 'AR':
      return faker.string.alphanumeric({ length: 4, casing: 'upper' }) + faker.string.numeric(4)
    default:
      return faker.string.numeric(5)
  }
}

/**
 * Generate contact information
 */
function generateContact(countryCode: CountryCode): ContactInfo {
  const phonePrefix: Record<CountryCode, string> = {
    BR: '+55',
    MX: '+52',
    CL: '+56',
    CO: '+57',
    PE: '+51',
    AR: '+54',
    US: '+1'
  }

  return {
    email: faker.internet.email(),
    phone: `${phonePrefix[countryCode] || '+1'} ${faker.string.numeric(2)} ${faker.string.numeric(4)}-${faker.string.numeric(4)}`,
    website: faker.helpers.maybe(() => faker.internet.url(), { probability: 0.7 })
  }
}

/**
 * Generate base statistics (will be updated with transaction data later)
 */
function generateBaseStatistics(): MerchantStatistics {
  return {
    totalTransactions: 0,
    totalRevenue: 0,
    averageTicket: 0,
    successRate: 0,
    chargebackRate: 0,
    refundRate: 0,
    monthlyVolume: [],
    lastTransactionDate: undefined
  }
}

/**
 * Generate a single merchant
 */
function generateMerchant(_index: number): Merchant {
  const id = `merch_${faker.string.alphanumeric(8)}`
  const merchantUuid = faker.string.uuid()
  const { code: countryCode, timezone } = generateCountry()
  const status = generateStatus()
  const category = faker.helpers.arrayElement(MERCHANT_CONFIG.CATEGORIES)

  const companyName = faker.company.name()
  const registeredAt = faker.date.between({
    from: '2020-01-01',
    to: new Date()
  }).toISOString()

  const now = new Date().toISOString()

  return {
    id,
    merchantId: `MID${faker.string.numeric(10)}`,
    merchantUuid,

    name: companyName,
    legalName: `${companyName} ${faker.helpers.arrayElement(['LLC', 'Inc', 'Ltd', 'Corp', 'SA', 'Ltda'])}`,
    description: faker.company.catchPhrase(),
    logo: faker.helpers.maybe(() => faker.image.urlLoremFlickr({ category: 'business' }), { probability: 0.6 }),
    status,
    category,

    country: countryCode,
    address: generateAddress(countryCode),

    businessInfo: {
      taxId: faker.string.alphanumeric(14),
      registrationNumber: faker.string.alphanumeric(12),
      legalType: faker.helpers.arrayElement(MERCHANT_CONFIG.LEGAL_TYPES),
      industry: faker.helpers.arrayElement(MERCHANT_CONFIG.INDUSTRIES),
      foundedYear: faker.number.int({ min: 1990, max: 2023 })
    },

    contact: generateContact(countryCode),

    bankingInfo: {
      bankName: faker.company.name() + ' Bank',
      bankCode: faker.string.numeric(3),
      accountNumber: faker.string.numeric(10),
      accountType: faker.helpers.arrayElement(['checking', 'savings'] as const),
      routingNumber: faker.string.numeric(9),
      beneficiaryName: companyName
    },

    compliance: {
      kycStatus: status === 'active' ? 'approved' : faker.helpers.arrayElement(['pending', 'approved', 'rejected', 'expired'] as const),
      kycApprovedAt: status === 'active' ? faker.date.recent({ days: 365 }).toISOString() : undefined,
      amlStatus: faker.helpers.arrayElement(['clear', 'review', 'flagged'] as const),
      riskLevel: faker.helpers.arrayElement(['low', 'medium', 'high'] as const),
      lastReviewDate: faker.date.recent({ days: 90 }).toISOString()
    },

    settings: {
      autoSettlement: faker.datatype.boolean({ probability: 0.7 }),
      settlementCycle: faker.helpers.arrayElement(['daily', 'weekly', 'biweekly', 'monthly'] as const),
      settlementDays: faker.helpers.maybe(() => faker.number.int({ min: 1, max: 30 }), { probability: 0.3 }),
      notificationEmail: faker.internet.email(),
      webhookUrl: faker.helpers.maybe(() => faker.internet.url() + '/webhooks/a55', { probability: 0.5 }),
      timezone
    },

    statistics: generateBaseStatistics(),

    registeredAt,
    createdAt: registeredAt,
    updatedAt: now
  }
}

/**
 * Generate all merchants dataset
 */
export function generateMerchants(count: number = MERCHANT_CONFIG.TOTAL_COUNT): Merchant[] {
  // Set consistent seed for reproducible data
  faker.seed(MERCHANT_CONFIG.SEED)

  const merchants: Merchant[] = []

  for (let i = 0; i < count; i++) {
    merchants.push(generateMerchant(i))
  }

  // Sort by registration date (newest first)
  return merchants.sort((a, b) =>
    new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime()
  )
}

/**
 * Convert full merchant to list item
 */
export function toMerchantListItem(merchant: Merchant): MerchantListItem {
  return {
    id: merchant.id,
    merchantId: merchant.merchantId,
    merchantUuid: merchant.merchantUuid,
    name: merchant.name,
    legalName: merchant.legalName,
    status: merchant.status,
    category: merchant.category,
    country: merchant.country,
    contact: merchant.contact,
    logo: merchant.logo,
    statistics: {
      totalTransactions: merchant.statistics.totalTransactions,
      totalRevenue: merchant.statistics.totalRevenue,
      successRate: merchant.statistics.successRate
    },
    registeredAt: merchant.registeredAt
  }
}

/**
 * Get status label for display
 */
export function getMerchantStatusLabel(status: MerchantStatus): string {
  const labels: Record<MerchantStatus, string> = {
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended',
    pending: 'Pending',
    blocked: 'Blocked'
  }
  return labels[status] || status
}

/**
 * Get category label for display
 */
export function getMerchantCategoryLabel(category: MerchantCategory): string {
  const labels: Record<MerchantCategory, string> = {
    e_commerce: 'E-Commerce',
    retail: 'Retail',
    services: 'Services',
    digital_goods: 'Digital Goods',
    travel: 'Travel',
    food_delivery: 'Food Delivery',
    gaming: 'Gaming',
    subscription: 'Subscription',
    marketplace: 'Marketplace',
    other: 'Other'
  }
  return labels[category] || category
}

/**
 * NEW BFF API DATA GENERATORS
 * For GET /bff/merchant/ endpoint
 */

/**
 * MCC code mapping for merchant categories
 */
export const MCC_CODES: Record<MerchantCategory, { code: string, description: string }[]> = {
  e_commerce: [
    { code: '5411', description: 'Grocery Stores' },
    { code: '5311', description: 'Department Stores' },
    { code: '5999', description: 'Miscellaneous Retail' }
  ],
  retail: [
    { code: '5541', description: 'Service Stations' },
    { code: '5651', description: 'Family Clothing Stores' },
    { code: '5691', description: 'Clothing Stores' }
  ],
  services: [
    { code: '7299', description: 'Miscellaneous Services' },
    { code: '7399', description: 'Business Services' },
    { code: '8999', description: 'Professional Services' }
  ],
  digital_goods: [
    { code: '5815', description: 'Digital Goods Media' },
    { code: '5816', description: 'Digital Goods Games' },
    { code: '5817', description: 'Digital Goods Applications' }
  ],
  travel: [
    { code: '4511', description: 'Airlines' },
    { code: '4722', description: 'Travel Agencies' },
    { code: '7011', description: 'Hotels/Motels' }
  ],
  food_delivery: [
    { code: '5812', description: 'Restaurants' },
    { code: '5814', description: 'Fast Food' },
    { code: '5811', description: 'Caterers' }
  ],
  gaming: [
    { code: '7994', description: 'Video Game Arcades' },
    { code: '7993', description: 'Gambling' },
    { code: '7995', description: 'Lottery' }
  ],
  subscription: [
    { code: '4899', description: 'Cable/Satellite Services' },
    { code: '5968', description: 'Subscription Services' },
    { code: '7841', description: 'Video Rental' }
  ],
  marketplace: [
    { code: '5399', description: 'General Merchandise' },
    { code: '5310', description: 'Discount Stores' },
    { code: '5331', description: 'Variety Stores' }
  ],
  other: [
    { code: '7999', description: 'Recreation Services' },
    { code: '8099', description: 'Health Services' },
    { code: '9999', description: 'Other' }
  ]
}

const CURRENCIES: CurrencyCode[] = ['BRL', 'USD', 'MXN', 'CLP', 'ARS']

const CARD_FLAGS = ['Visa', 'Mastercard', 'Amex', 'Elo', 'Hipercard']

/**
 * Generate daily data for the last 30 days
 */
export function generateMerchantDailyData(_merchantIndex: number): MerchantDailyData[] {
  const dailyData: MerchantDailyData[] = []
  const today = new Date(2026, 0, 15) // January 15, 2026

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)

    // Random transaction volume with some zero days
    const hasActivity = Math.random() > 0.1 // 90% chance of activity
    const volume_usd = hasActivity
      ? faker.number.float({ min: 500, max: 25000, fractionDigits: 2 })
      : 0
    const transaction_count = hasActivity
      ? faker.number.int({ min: 5, max: 100 })
      : 0

    dailyData.push({
      date: date.toISOString().split('T')[0]!,
      volume_usd,
      transaction_count
    })
  }

  return dailyData
}

/**
 * Generate volume by currency breakdown
 */
export function generateMerchantVolumeByCurrency(): MerchantVolumeByCurrency[] {
  const currencyCount = faker.number.int({ min: 1, max: 4 })
  const selectedCurrencies = faker.helpers.arrayElements(CURRENCIES, currencyCount)

  return selectedCurrencies.map(currency => ({
    currency,
    transaction_count: faker.number.int({ min: 50, max: 500 }),
    volume: faker.number.float({ min: 10000, max: 1000000, fractionDigits: 2 })
  })).sort((a, b) => b.volume - a.volume)
}

/**
 * Generate volume by card flag breakdown
 */
export function generateMerchantVolumeByFlag(): MerchantVolumeByFlag[] {
  const flagCount = faker.number.int({ min: 2, max: 5 })
  const selectedFlags = faker.helpers.arrayElements(CARD_FLAGS, flagCount)

  return selectedFlags.map(flag => ({
    flag,
    transaction_count: faker.number.int({ min: 30, max: 400 }),
    volume_usd: faker.number.float({ min: 5000, max: 500000, fractionDigits: 2 })
  })).sort((a, b) => b.volume_usd - a.volume_usd)
}

/**
 * Generate merchant configuration flags
 */
export function generateMerchantConfig(): MerchantConfig {
  return {
    anti_fraud_enabled: faker.datatype.boolean({ probability: 0.8 }),
    anti_fraud_by_pass_enabled: faker.datatype.boolean({ probability: 0.2 }),
    authentication_enabled: faker.datatype.boolean({ probability: 0.75 }),
    authentication_forced_enabled: faker.datatype.boolean({ probability: 0.3 }),
    authentication_dataonly_enabled: faker.datatype.boolean({ probability: 0.4 }),
    tokenization_enabled: faker.datatype.boolean({ probability: 0.6 }),
    zeroauth_enabled: faker.datatype.boolean({ probability: 0.5 }),
    orchestration_enabled: faker.datatype.boolean({ probability: 0.45 })
  }
}

/**
 * Get MCC code for a merchant category
 */
export function getMerchantMCC(category: MerchantCategory): string {
  const codes = MCC_CODES[category] || MCC_CODES.other
  return faker.helpers.arrayElement(codes).code
}

/**
 * Convert existing Merchant to MerchantWithMetrics format
 */
export function toMerchantWithMetrics(merchant: Merchant, index: number): MerchantWithMetrics {
  const dailyData = generateMerchantDailyData(index)
  const volumeByCurrency = generateMerchantVolumeByCurrency()
  const volumeByFlag = generateMerchantVolumeByFlag()
  const merchantConfig = generateMerchantConfig()

  // Calculate totals from daily data
  const total_volume_usd = dailyData.reduce((sum, day) => sum + day.volume_usd, 0)
  const transaction_count = dailyData.reduce((sum, day) => sum + day.transaction_count, 0)

  // Map status to new format
  const statusMap: Record<MerchantStatus, 'active' | 'inactive' | 'suspended' | 'blocked'> = {
    active: 'active',
    inactive: 'inactive',
    suspended: 'suspended',
    pending: 'inactive',
    blocked: 'blocked'
  }

  return {
    id: parseInt(merchant.id.replace('merch_', ''), 16) % 1000000 || index + 1,
    merchant_uuid: merchant.merchantUuid,
    soft_description: merchant.name,
    mcc: getMerchantMCC(merchant.category),
    website: merchant.contact.website || null,
    status: statusMap[merchant.status],
    is_blocked: merchant.status === 'blocked',
    total_volume_usd: Number(total_volume_usd.toFixed(2)),
    transaction_count,
    daily_data: dailyData,
    volume_by_currency: volumeByCurrency,
    volume_by_flag: volumeByFlag,
    merchant_config: merchantConfig
  }
}
